import React from 'react';
import { Card, Typography, Table, Tag, Avatar, Empty } from 'antd';
import { 
  BuildOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ExclamationCircleOutlined,
  TeamOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  BranchesOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../../router/all_routes';
import moment from 'moment';
import 'moment/locale/tr';
import { Company } from '../../../../types/company.types';
import { TopPayingCompany } from '../../../../types/super-admin.types';

const { Title, Text } = Typography;

type CompanyLike = Omit<Partial<Company> & Partial<TopPayingCompany>, 'createdAt' | 'status'> & {
  createdAt?: string | Date;
  status?: string;
};

interface CompanyListTableProps {
  companies: CompanyLike[];
  loading?: boolean;
  title?: string;
}

const CompanyListTable: React.FC<CompanyListTableProps> = ({ companies, loading = false, title = "Şirket Listesi" }) => {
  moment.locale('tr');

  const colorFromString = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
  };

  const getStatusInfo = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'active': return { icon: <CheckCircleOutlined />, text: 'Aktif', color: 'success' };
      case 'inactive': return { icon: <CloseCircleOutlined />, text: 'Pasif', color: 'default' };
      case 'expired': return { icon: <CloseCircleOutlined />, text: 'Süresi Dolmuş', color: 'error' };
      case 'trial': return { icon: <CheckCircleOutlined />, text: 'Deneme', color: 'processing' };
      case 'in_grace_period': return { icon: <ExclamationCircleOutlined />, text: 'Tolerans Süresinde', color: 'warning' };
      default: return { icon: <ExclamationCircleOutlined />, text: status, color: 'default' };
    }
  };

  const columns = [
    {
      title: 'Şirket',
      key: 'company',
      render: (_: any, record: CompanyLike) => (
        <div className="d-flex align-items-center">
          <Avatar 
            style={{ backgroundColor: colorFromString(record.name || ''), marginRight: '10px' }}
            icon={<BuildOutlined />}
          />
          <div>
            <Link to={all_routes.superAdminCompanyDetails.replace(':id', (record.id || '').toString())} className="fw-bold">
              {record.name}
            </Link>
            <div className="text-muted small">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'İletişim',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone ? <div><PhoneOutlined className="me-1" /> {phone}</div> : <Text type="secondary">Belirtilmemiş</Text>,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status?: string) => {
        if (!status) return <Tag>Bilinmiyor</Tag>;
        const info = getStatusInfo(status);
        return (
          <Tag icon={info.icon} color={info.color as any}>
            {info.text}
          </Tag>
        );
      },
    },
    {
      title: 'Çalışan/Şube',
      key: 'stats',
      render: (_: any, record: CompanyLike) => (
        <div>
          <div><TeamOutlined className="me-1" /> {record.userCount ?? record.users?.length ?? 0} Çalışan</div>
          <div><BranchesOutlined className="me-1" /> {record.branchCount ?? record.branches?.length ?? 0} Şube</div>
        </div>
      ),
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string | Date) => (
        <div>
          <CalendarOutlined className="me-1" /> 
          {moment(date).format('DD.MM.YYYY')}
          <div className="text-muted small">{moment(date).fromNow()}</div>
        </div>
      ),
    },
  ];

  return (
    <Card 
      title={<Title level={5}>{title}</Title>} 
      extra={<Link to={all_routes.superAdminCompanies}>Tümünü Gör</Link>}
      className="recent-companies-card mb-4"
      bordered={false}
    >
      {companies.length > 0 ? (
        <Table 
          dataSource={companies} 
          columns={columns} 
          rowKey="id" 
          pagination={false}
          loading={loading}
          className="modern-table"
          rowClassName="table-row-hover"
        />
      ) : (
        <Empty description="Henüz şirket bulunmamaktadır" />
      )}
    </Card>
  );
};

export default CompanyListTable; 